import type { Layout } from '../../data/types';
import type { PolygonWithHoles, Vec2 } from '../../geo/types';
import type { GroundPiece } from '../../site/types';
import type { Lot } from '../fabric/types';
import type { AABB } from '../../geo/types';
import { fromRing, intersection, union } from '../../geo/clip';
import { ringBounds } from '../../geo/polygon';
import { lotKey } from '../fabric/lotKey';
import { lotSides } from '../fabric/lotFrame';
import { conformedTo } from './conform';
import { claimsOf, type Claim, type Front } from './pavingClaims';
import {
  boxesMeet,
  framedStreets,
  ownedGround,
  type FramedStreet,
  type Part,
} from './pavingGround';
import type { FrameSpan, StreetFrame } from './streetFrame';

/**
 * Seeds look-2's paving pattern reads (aFacade.z): a house front's is its lot's index, the same on
 * every piece before that front; the rest take one of their own from `open` up, one for each stretch
 * with no house, within each ground piece it crosses, and one for each square.
 */
export const PAVING_SEED = { open: 2 ** 19, end: 2 ** 20 } as const;

/** A piece of paving, the part of a street's or square's ground that one house front paves. */
export interface PavingPiece {
  readonly polygon: PolygonWithHoles;
  /** The ground piece it lies in, whose style and edge id it keeps. */
  readonly ground: GroundPiece;
  readonly seed: number;
  /** The key of the house whose front it lies before (lotKey.ts), or null. */
  readonly front: string | null;
  /** The id of the street it lies on, or null on a square. */
  readonly street: string | null;
  /** The exact frame window and left edge used to restore an offline paving plan. */
  readonly coordinates?: PavingCoordinates;
  /** Metres along its street's centre line and across the street from its left edge; layout metres on a square. */
  readonly coordsAt: (p: Vec2) => Vec2;
}

export interface PavingCoordinates {
  readonly near: readonly [number, number];
  readonly edgeAt: number;
}

/** The paving, and the ground the ground feature draws around it. */
export interface PavedGround {
  readonly pieces: readonly PavingPiece[];
  /**
   * The site's ground pieces in order, each paved street piece and each square left out, the rest
   * whole but given the paving's vertices on their edges.
   */
  readonly ground: readonly GroundPiece[];
}

export interface PavingInput {
  readonly streets: Layout['streets'];
  readonly ground: readonly GroundPiece[];
  /** The house lots (fabricLots.ts `planLots().houses`). */
  readonly houses: readonly Lot[];
}

// Clipper rounds each crossing it computes to the millimetre, so pieces two operations cut apart
// can miss each other's vertices along the line they share by up to this much.
const CONFORM_M = 2e-3;

/* A piece before a front carries the lot's index as its seed; any other piece the name of the
   stretch it belongs to, which gets a seed once all are known. */
type Draft = Omit<PavingPiece, 'seed'> & {
  readonly seed: number | null;
  readonly stretch: string;
};

function frontsOf(lots: readonly Lot[], frame: StreetFrame): Front[] {
  return lots.map((lot) => {
    if (lot.index >= PAVING_SEED.open)
      throw new Error(`Lot ${lotKey(lot)} is past the paving seeds`);
    const { left, right } = lotSides(lot);
    const [a, b] = [frame.coordsOf(left.from)[0], frame.coordsOf(right.from)[0]];
    return {
      key: lotKey(lot),
      seed: lot.index,
      side: lot.side,
      from: Math.min(a, b),
      to: Math.max(a, b),
    };
  });
}

/* A claim's spans in runs that each lie on one leg of the frame: within a leg the frame's metres
   are near enough linear for a triangle to carry them, across a bend they are not. */
function runsOnLegs(spans: readonly FrameSpan[], bends: ReadonlySet<number>): FrameSpan[][] {
  const runs: FrameSpan[][] = [];
  for (const span of spans) {
    const last = runs[runs.length - 1];
    if (last && !bends.has(span.from)) last.push(span);
    else runs.push([span]);
  }
  return runs;
}

function claimPieces(
  { street, frame }: FramedStreet,
  [claim, index]: readonly [Claim, number],
  parts: readonly Part[]
): Draft[] {
  const edgeAt = street.width / 2;
  return runsOnLegs(claim.spans, new Set(frame.bends)).flatMap((run) => {
    const ring = frame.outline(run);
    const box = ringBounds(ring);
    const near: readonly [number, number] = [run[0]!.from, run[run.length - 1]!.to];
    const coordsAt = (p: Vec2): Vec2 => {
      const [s, t] = frame.coordsOf(p, near);
      return [s, edgeAt - t];
    };
    // Body/end ownership cuts can divide one claim on the same ground into
    // touching polygons. Restore that claim's union before welding: otherwise
    // the redundant seam can leave a sub-millimetre triangle at a bank corner.
    // Only this run's owner, coordinate window and ground are combined, so no
    // house frontage or bend boundary loses its own paving coordinates.
    const byGround = new Map<GroundPiece, PolygonWithHoles[]>();
    for (const part of parts.filter((part) => boxesMeet(part.box, box))) {
      const clipped = intersection([fromRing(ring)], [part.polygon]);
      if (clipped.length > 0)
        byGround.set(part.ground, [...(byGround.get(part.ground) ?? []), ...clipped]);
    }
    return [...byGround].flatMap(([ground, polygons]) =>
      (polygons.length > 1 ? union(polygons) : polygons).map((polygon) => ({
          polygon,
          ground,
          seed: claim.owner?.seed ?? null,
          stretch: `${street.id}:${String(index)}:${String(ground.index)}`,
          front: claim.owner?.key ?? null,
          street: street.id,
          coordinates: { near, edgeAt },
          coordsAt,
        }))
    );
  });
}

function streetDrafts(input: PavingInput): Draft[] {
  const streets = framedStreets(input.streets);
  const owned = ownedGround(streets, input.ground);
  const lots = new Map<string, Lot[]>();
  for (const lot of input.houses) lots.set(lot.sourceId, [...(lots.get(lot.sourceId) ?? []), lot]);
  return streets.flatMap((paved, k) => {
    const parts = owned[k]!;
    if (parts.length === 0) return [];
    const fronts = frontsOf(lots.get(paved.street.id) ?? [], paved.frame);
    const claims = claimsOf(fronts, paved.frame.length, paved.frame.reach, paved.frame.bends);
    return claims.flatMap((claim, index) => claimPieces(paved, [claim, index], parts));
  });
}

function squareDrafts(ground: readonly GroundPiece[]): Draft[] {
  return ground
    .filter((piece) => piece.kind === 'square')
    .map((piece) => ({
      polygon: piece.polygon,
      ground: piece,
      seed: null,
      stretch: `square:${piece.index}`,
      front: null,
      street: null,
      coordsAt: (p: Vec2): Vec2 => [p[0], p[1]],
    }));
}

function seeded(drafts: readonly Draft[]): PavingPiece[] {
  const stretches = [...new Set(drafts.filter((d) => d.seed === null).map((d) => d.stretch))];
  if (PAVING_SEED.open + stretches.length > PAVING_SEED.end) {
    throw new Error('More stretches of paving than paving seeds');
  }
  const seeds = new Map(stretches.map((stretch, k) => [stretch, PAVING_SEED.open + k] as const));
  return drafts.map(({ stretch, seed, ...piece }) => ({
    ...piece,
    seed: seed ?? seeds.get(stretch)!,
  }));
}

function boundsOf(polygons: readonly PolygonWithHoles[]): AABB | null {
  if (polygons.length === 0) return null;
  const boxes = polygons.map((polygon) => ringBounds(polygon.outer));
  return {
    minX: Math.min(...boxes.map((b) => b.minX)),
    minY: Math.min(...boxes.map((b) => b.minY)),
    maxX: Math.max(...boxes.map((b) => b.maxX)),
    maxY: Math.max(...boxes.map((b) => b.maxY)),
  };
}

/* The ground the paving leaves: only ground pieces near the paving can share an edge with it, so
   only those are fitted to its vertices. */
function groundAround(
  ground: readonly GroundPiece[],
  paved: ReadonlySet<GroundPiece>,
  fitted: ReadonlyMap<GroundPiece, PolygonWithHoles>
): GroundPiece[] {
  return ground.flatMap((piece) => {
    if (paved.has(piece)) return [];
    const polygon = fitted.get(piece) ?? piece.polygon;
    return [polygon === piece.polygon ? piece : { ...piece, polygon }];
  });
}

/**
 * The paving of the streets and squares (streetscape plan section 5.1), and the ground left around
 * it: each street's ground, cut into one piece per house front, from the
 * front to the centre line where both sides are built and across the street where the other side
 * is not; frontage with no house on either side, and each square, is paved apart. The pieces tile
 * the paved ground; they and the ground next to them share every vertex along the lines
 * where they meet, so no crack opens there.
 */
export function pavedGround(input: PavingInput): PavedGround {
  const drafts = [...streetDrafts(input), ...squareDrafts(input.ground)];
  const paved = new Set(drafts.map((draft) => draft.ground));
  const box = boundsOf(drafts.map((draft) => draft.polygon));
  const near = input.ground.filter(
    (piece) => !paved.has(piece) && box !== null && boxesMeet(ringBounds(piece.polygon.outer), box)
  );
  const { pieces, fixed } = conformedTo(
    drafts.map((draft) => draft.polygon),
    near.map((piece) => piece.polygon),
    CONFORM_M
  );
  const kept = drafts
    .map((draft, k) => ({ ...draft, polygon: pieces[k]! }))
    .filter((draft) => draft.polygon.outer.length >= 3);
  const fitted = new Map(near.map((piece, k) => [piece, fixed[k]!] as const));
  return { pieces: seeded(kept), ground: groundAround(input.ground, paved, fitted) };
}

/** The paving alone (`pavedGround`). */
export function pavingPieces(input: PavingInput): readonly PavingPiece[] {
  return pavedGround(input).pieces;
}

/** Ground with nothing paved on it: the site's own, for as long as the paving is not drawn. */
export function unpavedGround(ground: readonly GroundPiece[]): PavedGround {
  return { pieces: [], ground };
}
